import React, { createContext, useContext, useState, useEffect, useRef, useCallback } from 'react';
import { API_BASE, SOCKET_URL } from '../apiConfig';
import { useAuth } from './AuthContext';
import { importSharedKey, encryptMessage, decryptMessage } from '../utils/e2eeCrypto';

const ChatContext = createContext({
  socket: null,
  conversations: [],
  totalUnread: 0,
  onlineUsers: [],
  typingUsers: {},
  activeChats: [],
  isWidgetOpen: false,
  loadConversations: () => {},
  openChat: () => {},
  closeChat: () => {},
  toggleWidget: () => {},
  setWidgetOpen: () => {},
  sendMessage: () => {},
  markAsRead: () => {},
  startTyping: () => {},
  stopTyping: () => {},
  e2eeReady: false,
  decryptMessages: async (msgs) => msgs,
  decryptMsg: async (msg) => msg,
});

export const ChatProvider = ({ children }) => {
  const { user, sessionId } = useAuth();
  const socketRef = useRef(null);
  const [conversations, setConversations] = useState([]);
  const conversationsRef = useRef([]);
  conversationsRef.current = conversations;
  const [totalUnread, setTotalUnread] = useState(0);
  const [onlineUsers, setOnlineUsers] = useState([]);
  const [typingUsers, setTypingUsers] = useState({}); // { chatId: { userId, userName } }
  const [activeChats, setActiveChats] = useState([]); // open chat windows in widget (max 2)
  const [isWidgetOpen, setIsWidgetOpen] = useState(false);

  // ── E2EE Per-Chat Keys ─────────────────────────────────────────────────────
  const chatKeyCacheRef = useRef(new Map()); // chatId -> CryptoKey
  const [e2eeReady, setE2eeReady] = useState(false);

  useEffect(() => {
    if (!user?.user_id || !sessionId) {
      chatKeyCacheRef.current = new Map();
      setE2eeReady(false);
      return;
    }
    setE2eeReady(true);
  }, [user?.user_id, sessionId]);

  const getChatKey = useCallback(async (chatId) => {
    const parsedChatId = Number(chatId);
    if (!parsedChatId || !sessionId) return null;

    const existing = chatKeyCacheRef.current.get(parsedChatId);
    if (existing) return existing;

    const res = await fetch(`${API_BASE}/e2ee/chat-key?chat_id=${parsedChatId}`, {
      headers: { 'x-session-id': sessionId }
    });

    if (!res.ok) {
      throw new Error('Unable to fetch chat key');
    }

    const data = await res.json();
    if (!data?.key) return null;

    const imported = await importSharedKey(data.key);
    chatKeyCacheRef.current.set(parsedChatId, imported);
    return imported;
  }, [sessionId]);

  // Decrypt a single message object
  const decryptMsg = useCallback(async (msg) => {
    if (!msg.encrypted || !msg.iv) return msg;
    const chatId = Number(msg.chat_id || msg.chatId);
    if (!chatId) return msg;

    try {
      const key = await getChatKey(chatId);
      if (!key) return msg;
      const plaintext = await decryptMessage(msg.text, msg.iv, key);
      return { ...msg, text: plaintext, _decrypted: true };
    } catch {
      return { ...msg, text: '🔒 This message cannot be decrypted', _decryptFailed: true };
    }
  }, [getChatKey]);

  // Decrypt an array of messages
  const decryptMessages = useCallback(async (messages) => {
    return Promise.all(messages.map(m => decryptMsg(m)));
  }, [decryptMsg]);

  // ── Socket Connection (socket.io loaded on demand) ─────────────────────────
  useEffect(() => {
    if (!user?.user_id) {
      if (socketRef.current) {
        socketRef.current.disconnect();
        socketRef.current = null;
      }
      return;
    }

    let cancelled = false;

    import('socket.io-client').then(({ default: io }) => {
      if (cancelled) return;

      const s = io(SOCKET_URL, {
        auth: { sessionId },
        transports: ['websocket', 'polling'],
        reconnection: true,
        reconnectionDelay: 1000,
        reconnectionAttempts: Infinity,
      });
      socketRef.current = s;

      s.on('connect', () => {
        console.log('[ChatContext] Socket connected');
        s.emit('userOnline');
      });

      s.on('reconnect', () => {
        s.emit('userOnline');
      });

      // Online users
      s.on('onlineUsers', (users) => {
        setOnlineUsers(users.map(Number));
      });

      // New message received (via personal room)
      s.on('messageNotification', async ({ chatId, message }) => {
        // Attempt to decrypt the preview text
        let previewText = message.text;
        let decrypted = false;
        if (message.encrypted && message.iv) {
          try {
            const key = await getChatKey(chatId);
            if (key) {
              previewText = await decryptMessage(message.text, message.iv, key);
              decrypted = true;
            }
          } catch { /* fall through */ }
        }
        // Update conversation list
        setConversations(prev => {
          const updated = prev.map(c => {
            if (c.chat_id === chatId) {
              return {
                ...c,
                last_message_text: previewText,
                last_message_encrypted: message.encrypted ? 1 : 0,
                _preview_decrypted: decrypted,
                last_message_sender_id: message.sender_id,
                last_message_time: message.timestamp,
                unread_count: c.unread_count + 1,
              };
            }
            return c;
          });
          updated.sort((a, b) => {
            const tA = a.last_message_time || a.updated_at;
            const tB = b.last_message_time || b.updated_at;
            return new Date(tB) - new Date(tA);
          });
          return updated;
        });
        setTotalUnread(prev => prev + 1);
      });

      // Typing indicators
      s.on('userTyping', ({ chatId, userId, userName }) => {
        setTypingUsers(prev => ({ ...prev, [chatId]: { userId, userName } }));
      });
      s.on('userStopTyping', ({ chatId }) => {
        setTypingUsers(prev => {
          const next = { ...prev };
          delete next[chatId];
          return next;
        });
      });
    });

    return () => {
      cancelled = true;
      if (socketRef.current) {
        socketRef.current.disconnect();
        socketRef.current = null;
      }
    };
  }, [user?.user_id, sessionId, getChatKey]);

  // ── Load Conversations ─────────────────────────────────────────────────────
  const loadConversations = useCallback(async () => {
    if (!user?.user_id) return;
    try {
      const res = await fetch(`${API_BASE}/user-chats`, {
        headers: { 'x-session-id': sessionId }
      });
      const data = await res.json();
      const chats = data.chats || [];

      // Decrypt last-message previews for encrypted conversations
      await Promise.all(chats.map(async (c) => {
        if (c.last_message_encrypted && c.last_message_text && c.last_message_iv && c.chat_id) {
          try {
            const key = await getChatKey(c.chat_id);
            if (key) {
              c.last_message_text = await decryptMessage(c.last_message_text, c.last_message_iv, key);
              c._preview_decrypted = true;
            }
          } catch { /* leave encrypted text as-is */ }
        }
      }));

      setConversations(chats);
      const total = chats.reduce((sum, c) => sum + (c.unread_count || 0), 0);
      setTotalUnread(total);
    } catch (err) {
      console.error('[ChatContext] Failed to load conversations:', err);
    }
  }, [user?.user_id, sessionId, getChatKey]);

  // Load conversations on mount, when E2EE becomes ready, and periodically
  useEffect(() => {
    if (!user?.user_id) return;
    loadConversations();
    const interval = setInterval(loadConversations, 30000); // refresh every 30s
    return () => clearInterval(interval);
  }, [user?.user_id, loadConversations, e2eeReady]);

  // ── Open/Close Chat Windows ────────────────────────────────────────────────
  const openChat = useCallback((conversation) => {
    setActiveChats(prev => {
      // Don't duplicate
      if (prev.some(c => c.chat_id === conversation.chat_id)) return prev;
      // Max 2 open chat windows, push out oldest
      const next = [...prev, conversation];
      if (next.length > 2) next.shift();
      return next;
    });
    setIsWidgetOpen(true);
  }, []);

  const closeChat = useCallback((chatId) => {
    setActiveChats(prev => prev.filter(c => c.chat_id !== chatId));
    if (socketRef.current) {
      socketRef.current.emit('leaveChat', chatId);
    }
  }, []);

  const toggleWidget = useCallback(() => {
    setIsWidgetOpen(prev => !prev);
  }, []);

  // ── Send Message (with E2EE) ────────────────────────────────────────────────
  const sendMessage = useCallback(async ({ chatId, receiverId, text }) => {
    if (!socketRef.current || !user?.user_id || !text?.trim()) return;
    const trimmed = text.trim();

    // Optimistically update the conversation preview with plaintext
    setConversations(prev => {
      const updated = prev.map(c =>
        c.chat_id === chatId
          ? { ...c, last_message_text: trimmed, last_message_sender_id: user.user_id, last_message_time: new Date().toISOString(), _preview_decrypted: true, last_message_encrypted: 0 }
          : c
      );
      updated.sort((a, b) => new Date(b.last_message_time || b.updated_at) - new Date(a.last_message_time || a.updated_at));
      return updated;
    });

    // Attempt encryption
    if (e2eeReady) {
      try {
        const key = await getChatKey(chatId);
        if (!key) throw new Error('Missing chat key');
        const { ciphertext, iv } = await encryptMessage(trimmed, key);
        socketRef.current.emit('sendMessage', {
          chatId,
          receiverId,
          text: ciphertext,
          encrypted: true,
          iv,
        });
        return;
      } catch (err) {
        console.error('[E2EE] Encryption failed, sending plaintext:', err);
      }
    }

    // Fallback: send plaintext
    socketRef.current.emit('sendMessage', {
      chatId,
      receiverId,
      text: trimmed,
    });
  }, [user?.user_id, e2eeReady, getChatKey]);

  // ── Mark As Read ───────────────────────────────────────────────────────────
  const markAsRead = useCallback((chatId) => {
    if (!socketRef.current || !user?.user_id) return;
    socketRef.current.emit('markRead', { chatId });
    // Optimistically update
    setConversations(prev =>
      prev.map(c => c.chat_id === chatId ? { ...c, unread_count: 0 } : c)
    );
    setTotalUnread(prev => {
      const chat = conversationsRef.current.find(c => c.chat_id === chatId);
      return Math.max(0, prev - (chat?.unread_count || 0));
    });
  }, [user?.user_id]);

  // ── Typing ─────────────────────────────────────────────────────────────────
  const startTyping = useCallback((chatId) => {
    if (!socketRef.current || !user) return;
    socketRef.current.emit('typing', {
      chatId,
      userName: user.forename || 'Someone',
    });
  }, [user]);

  const stopTyping = useCallback((chatId) => {
    if (!socketRef.current || !user) return;
    socketRef.current.emit('stopTyping', { chatId });
  }, [user]);

  return (
    <ChatContext.Provider value={{
      socket: socketRef.current,
      conversations,
      totalUnread,
      onlineUsers,
      typingUsers,
      activeChats,
      isWidgetOpen,
      loadConversations,
      openChat,
      closeChat,
      toggleWidget,
      setWidgetOpen: setIsWidgetOpen,
      sendMessage,
      markAsRead,
      startTyping,
      stopTyping,
      e2eeReady,
      decryptMessages,
      decryptMsg,
    }}>
      {children}
    </ChatContext.Provider>
  );
};

export const useChat = () => useContext(ChatContext);
